import { clearAuthStorage } from "@/config/authConfig";
import { HamburgerIcon } from "@/containers/Layout/MainLayout/styled";
import { MenuItemType } from "@/enums/MenuItemType";
import { routes } from "@/helpers";
import { generateMenuItems } from "@/helpers/generateMenuItems";
import { useAllRights } from "@/hooks";
import { IViperMenuItem } from "@/interfaces/IViperMenuItem";
import { useUserContext } from "@/providers";
import { useExternalLinksQuery } from "@/services";
import { useMsal } from "@azure/msal-react";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faBars } from "@fortawesome/pro-solid-svg-icons";
import { Drawer, Menu, MenuProps } from "antd";
import { useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

export const MobileNavMenu = () => {
  const { isImpersonating, resetImpersonation } = useUserContext();
  const { instance } = useMsal();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const intl = useIntl();
  const navigate = useNavigate();
  const { adminRights, hasSomeAdminRights, canViewCreateManageInitiatives } =
    useAllRights();
  const { data: externalLinks } = useExternalLinksQuery({});

  const user = instance.getActiveAccount();
  const logOut = (): Promise<void> => {
    clearAuthStorage();
    return instance.logoutRedirect();
  };

  const items: IViperMenuItem[] = [
    {
      key: "dashboard",
      type: MenuItemType.Item,
      title: intl.formatMessage({ id: "navbar.dashboard" }),
    },
    {
      key: "projects",
      type: MenuItemType.Item,
      title: intl.formatMessage({ id: "navbar.projects" }),
    },
    {
      key: "initiatives",
      type: MenuItemType.Item,
      title: intl.formatMessage({ id: "navbar.initiatives" }),
      visible: canViewCreateManageInitiatives,
    },
    {
      key: "goTo",
      type: MenuItemType.Group,
      title: intl.formatMessage({ id: "navbar.goTo" }),
    },
    {
      key: "goTo:GRS",
      type: MenuItemType.Item,
      title: intl.formatMessage({ id: "navbar.grs" }),
    },
    {
      key: "goTo:LRS",
      type: MenuItemType.Item,
      title: intl.formatMessage({ id: "navbar.lrs" }),
    },
    {
      key: "goTo:TimeTracker",
      type: MenuItemType.Item,
      title: intl.formatMessage({ id: "navbar.timeTracker" }),
    },
    {
      key: "goTo:PowerBI",
      type: MenuItemType.Item,
      title: intl.formatMessage({ id: "navbar.powerBI" }),
    },
    {
      key: "admin",
      type: MenuItemType.Group,
      title: intl.formatMessage({ id: "navbar.administration" }),
      visible: hasSomeAdminRights,
    },
    {
      key: "admin:projectRoles",
      type: MenuItemType.Item,
      title: intl.formatMessage({ id: "navbar.projectRoles" }),
      visible: adminRights.canViewProjectRoles,
    },
    {
      key: "admin:projectTemplates",
      type: MenuItemType.Item,
      title: intl.formatMessage({ id: "navbar.projectTemplates" }),
      visible: adminRights.canViewTemplates,
    },
    {
      key: "admin:approvalWorkFlows",
      type: MenuItemType.Item,
      title: intl.formatMessage({ id: "navbar.approvalWorkFlows" }),
      visible: adminRights.canViewApprovalWorkFlows,
    },
    {
      key: "admin:baselineDates",
      type: MenuItemType.Item,
      title: intl.formatMessage({ id: "navbar.baselineDates" }),
      visible: adminRights.canViewBaselineDates,
    },
    {
      key: "admin:adminConfig",
      type: MenuItemType.Item,
      title: intl.formatMessage({ id: "navbar.adminConfig" }),
      visible: adminRights.canViewBaselineDates,
    },
    {
      key: "admin:userManagement:users",
      type: MenuItemType.Item,
      title: intl.formatMessage({ id: "navbar.users" }),
      visible: adminRights.canViewUsers,
    },
    {
      key: "admin:userManagement:permissions",
      type: MenuItemType.Item,
      title: intl.formatMessage({ id: "navbar.permissions" }),
      visible: adminRights.canViewPermissions,
    },
    {
      key: "admin:userManagement:notificationGroups",
      type: MenuItemType.Item,
      title: intl.formatMessage({ id: "navbar.notificationGroups" }),
      visible: adminRights.canViewNotificationGroups,
    },
    {
      key: "admin:userManagement:conditionalAccess",
      type: MenuItemType.Item,
      title: intl.formatMessage({ id: "navbar.conditionalAccess" }),
      visible: adminRights.canViewPermissions,
    },
    {
      key: "admin:userManagement:userReplacement",
      type: MenuItemType.Item,
      title: intl.formatMessage({ id: "navbar.userReplacement" }),
      visible: adminRights.canViewPermissions,
    },
    {
      key: "admin:misc:apiDocumentation",
      type: MenuItemType.Item,
      title: intl.formatMessage({ id: "navbar.apiDocumentation" }),
      visible: adminRights.canViewApiDocumentation,
    },
    {
      key: "admin:misc:forms",
      type: MenuItemType.Item,
      title: intl.formatMessage({ id: "navbar.forms" }),
      visible: adminRights.canViewForms,
    },
    {
      key: "admin:misc:group",
      type: MenuItemType.Group,
      title: intl.formatMessage({ id: "navbar.misc" }),
    },
    {
      key: "user:profile",
      type: MenuItemType.Item,
      visible: adminRights.canViewUsers,
      title: intl.formatMessage({ id: "profile.profileText" }),
    },
    {
      key: "user:clearImpersonation",
      type: MenuItemType.Item,
      title: intl.formatMessage({ id: "profile.clearImpersonation" }),
      visible: isImpersonating,
    },
    {
      key: "user:signOut",
      type: MenuItemType.Item,
      title: intl.formatMessage({ id: "navbar.signOut" }),
    },
  ];

  const handleMenuAction: MenuProps["onClick"] = (e) => {
    switch (e.key) {
      case "dashboard":
        navigate(routes.dashboard);
        break;
      case "projects":
        navigate(routes.projects.list);
        break;
      case "initiatives":
        navigate(routes.initiatives.list);
        break;

      // External links
      case "goTo:GRS":
        window.open(externalLinks?.globalPrototypeRequestSystemUrl);
        break;
      case "goTo:LRS":
        window.open(externalLinks?.labRequestSystemUrl);
        break;
      case "goTo:TimeTracker":
        window.open(externalLinks?.timeTrackerUrl);
        break;
      case "goTo:PowerBI":
        window.open(externalLinks?.powerBIUrl);
        break;

      // Admin Project Links
      case "admin:projectRoles":
        navigate(routes.admin.projectRoles.list);
        break;
      case "admin:projectTemplates":
        navigate(routes.admin.projectTemplates.list);
        break;
      case "admin:approvalWorkFlows":
        navigate(routes.admin.approvalWorkFlows.list);
        break;
      case "admin:baselineDates":
        navigate(routes.admin.baselineDates.list);
        break;
      case "admin:adminConfig":
        navigate(routes.admin.adminConfig.list);
        break;

      // Admin User Management Links
      case "admin:userManagement:users":
        navigate(routes.admin.users.list);
        break;
      case "admin:userManagement:notificationGroups":
        navigate(routes.admin.notificationGroups.list);
        break;
      case "admin:userManagement:conditionalAccess":
        navigate(routes.admin.conditionalAccess.list);
        break;
      case "admin:userManagement:userReplacement":
        navigate(routes.admin.userReplacement.view);
        break;

      // Admin Misc Links
      case "admin:misc:apiDocumentation":
        navigate(routes.admin.apiDocumentation);
        break;
      case "admin:misc:forms":
        navigate(routes.admin.forms.list);
        break;

      // User Links
      case "user:profile": {
        const userId = user?.idTokenClaims?.oid;

        if (!userId) break;

        navigate(routes.admin.users.get(userId));
        break;
      }
      case "user:clearImpersonation":
        resetImpersonation();
        break;
      case "user:signOut":
        logOut();
        break;
      default:
        break;
    }
  };

  const onClick: MenuProps["onClick"] = (e) => {
    handleMenuAction(e);
  };

  return (
    <>
      <HamburgerIcon
        icon={faBars as IconProp}
        data-test-id="nav-menu-icon"
        onClick={(): void => setDrawerOpen(!drawerOpen)}
      />
      <Drawer
        data-test-id="mobile-nav-menu-drawer-open"
        placement="right"
        closable={true}
        maskClosable={true}
        onClose={(): void => setDrawerOpen(false)}
        open={drawerOpen}
      >
        <Menu
          data-test-id="mobile-nav-menu"
          mode="inline"
          onClick={onClick}
          selectedKeys={[]}
          items={generateMenuItems(items)}
        />
      </Drawer>
    </>
  );
};
